// @flow

import _ from 'lodash';

import { nowTimestamp, getDayDiff, getUnixTimestamp } from 'src/helpers';
import plans from 'src/constants/plans.json';
import plansPlus from 'src/constants/plans-plus.json';
import plansOld from 'src/constants/plans-old.json';
import plansOffer from 'src/constants/plans-offer.json';
import moment from 'moment';
import { DATE_FORMAT } from 'src/constants/language-setting';
/**
 * Get subscription status
 *
 * @param  {string} selectedPlan   selected plan
 * @param  {boolean} trialExpired   is expired
 * @param  {number} trialExpiresAt  expires at
 *
 * @return {object}                { subscription status, maximum product for selected plan, day left if status is trial}
 */
export function getSubscriptionStatus(tenantApp: Object) {
  const {
    tenantId,
    selectedPlan,
    trialExpired,
    trialExpiresAt,
    exceededPlan = false,
    exceededPlanAt = 0,
    discountCode,
    specialDiscountExpiredAt,
    allowToSubscribePlanV1,
    passwordProtectedOffer = {}
  } = tenantApp;

  // Get total products
  const currentTotalProducts = _.get(tenantApp, 'appParams.currentTotalProducts', 0);

  const codeApplied = _.get(discountCode, 'code', '');
  // Check apply special discount code
  const appliedSpecialDiscount = appliedSpecialDiscountCode(tenantId, selectedPlan, discountCode);

  const [todayDateString] = new Date().toISOString().split('T');
  const today = new Date(todayDateString).getTime() / 1000;
  const tomorrowDate = new Date();
  tomorrowDate.setHours(24, 0, 0);
  const tomorrow = tomorrowDate.getTime() / 1000;
  const amountDays = currentTotalProducts >= 50000 ? 15 : 7;
  const nextAmountDays = today + amountDays * 86400;

  let status = 'trial',
    dayLeft = 0,
    afterDayDiscountExpired;
  const now = nowTimestamp();

  const isShopifyPlus = _.get(tenantApp, 'externalInfo.plan_name', '') === 'shopify_plus';
  const allowToSubscribeRegularPlanV2 = _.get(tenantApp, 'allowToSubscribeRegularPlanV2', false);
  const allowToSubscribePlusPlanV2 = _.get(tenantApp, 'allowToSubscribePlusPlanV2', false);
  const subscribePlusPlanV2 =
    !allowToSubscribeRegularPlanV2 && (isShopifyPlus || allowToSubscribePlusPlanV2);

  const isExpiredRegularPlan = _.get(tenantApp, 'validateRegularPlanOffer.isExpired', false);
  const regularExpired = _.get(tenantApp, 'validateRegularPlanOffer.expiredAt', null);
  const appLanguage = _.get(tenantApp, 'appLanguage', 'en');

  const formatDateNumber = DATE_FORMAT[appLanguage].longDate;

  const regularExpiredAt = moment(new Date(regularExpired * 1e3)).format(formatDateNumber);
  // Password protected offer
  const passwordProtectedOfferExpired = _.get(passwordProtectedOffer, 'isExpired', false);
  const passwordProtectedOfferStartAt = _.get(passwordProtectedOffer, 'startAt', 0);
  // Check last 7 days until expire time (90 days from startAt)
  const passwordProtectedOfferExpiredSoon = today > passwordProtectedOfferStartAt + 82 * 86400;

  // Get number of product for selected plan if existing
  const {
    products: productsPlan = 0,
    label: planLabel,
    name: planName = ''
  } = getPlanInfo(selectedPlan);

  if (
    isExpiredRegularPlan ||
    (regularExpired && regularExpired <= now && planName.includes('regular'))
  ) {
    status = 'regularExpired';
  } else if (exceededPlan) {
    status = 'exceeded';
  } else if (!exceededPlan && exceededPlanAt > 0 && currentTotalProducts >= productsPlan * 1.1) {
    // Send notification for exceeded plan and total products > products plan * 1.1 (10%)
    status = 'exceededWithNotification';
  } else if (
    // Expired after 1 - 7days
    specialDiscountExpiredAt &&
    specialDiscountExpiredAt <= today &&
    specialDiscountExpiredAt > today - 7 * 86400
  ) {
    afterDayDiscountExpired = 'specialDiscountAfterExpired';
    if (selectedPlan) {
      status = 'subscribed';
    }
  } else if (
    specialDiscountExpiredAt &&
    specialDiscountExpiredAt <= today &&
    specialDiscountExpiredAt >= tomorrow
  ) {
    status = 'specialDiscountExpired';
  } else if (
    specialDiscountExpiredAt &&
    specialDiscountExpiredAt > today &&
    codeApplied === 'Boost-BFCM21'
  ) {
    dayLeft = getDayDiff(specialDiscountExpiredAt);

    status = 'specialDiscount2021ExpiredSoon';
  } else if (
    planName.includes('regular') &&
    (isShopifyPlus || allowToSubscribePlusPlanV2) &&
    regularExpired > now &&
    getDayDiff(regularExpired) <= 15
  ) {
    dayLeft = getDayDiff(regularExpired);
    status = 'regularExpiredSoon';
  } else if (
    appliedSpecialDiscount &&
    specialDiscountExpiredAt &&
    specialDiscountExpiredAt > today &&
    specialDiscountExpiredAt <= nextAmountDays
  ) {
    dayLeft = getDayDiff(specialDiscountExpiredAt);
    status = 'specialDiscountExpiredSoon';
  } else if (
    planName === 'offer0_1' &&
    (passwordProtectedOfferExpired ||
      (!passwordProtectedOfferExpired &&
        passwordProtectedOfferStartAt &&
        (today - passwordProtectedOfferStartAt) / 86400 > 90))
  ) {
    status = 'passwordProtectedOfferExpired';
  } else if (planName === 'offer0_1' && passwordProtectedOfferExpiredSoon) {
    status = 'passwordProtectedOfferExpiredSoon';
    dayLeft = 90 - Math.floor((today - passwordProtectedOfferStartAt) / 86400);
  } else if (selectedPlan) {
    status = 'subscribed';
  } else if (trialExpired) {
    status = 'expired';
  } else if (trialExpiresAt > now) {
    dayLeft = getDayDiff(trialExpiresAt);
    if (dayLeft <= 2) {
      status = 'expiredSoon';
    } else {
      status = 'trial';
    }
  }

  // Get correct plan based on total product
  const correctPlan = getCorrectPlan(
    currentTotalProducts,
    subscribePlusPlanV2,
    allowToSubscribePlanV1
  );

  // Exceeded with notifications' first 7 days flag
  let isFirst7Days = false;
  if (status === 'exceededWithNotification') {
    const [todayDateString] = new Date().toISOString().split('T');
    const today = new Date(todayDateString).getTime() / 1000;

    isFirst7Days = exceededPlanAt >= today - 7 * 86400;
  }

  return {
    status,
    productsPlan,
    dayLeft,
    regularExpiredAt,
    correctPlan,
    planLabel,
    afterDayDiscountExpired,
    planName,
    isFirst7Days,
    discountCode
  };
}

/**
 * Get minimum plan base on current total product of the store
 *
 * @param  {number} totalProducts current total product
 * @param  {boolean} isPlusPlan
 *
 * @return {object}               minimum plan
 */
export function getCorrectPlan(
  totalProducts: number,
  isPlusPlan: boolean,
  allowToSubscribePlanV1: boolean = false
) {
  const plansList = allowToSubscribePlanV1 ? plansOld : isPlusPlan ? plansPlus : plans;
  // Get selected plan
  let plan = _.find(plansList, item => {
    return item.products >= totalProducts;
  });

  if (!plan) {
    const keys = Object.keys(plansList);
    plan = plansList[_.last(keys)];
  }

  return plan;
}

/**
 * Get new corrected plan based on planId - use to subscribe new plan
 *
 * @param  {string} planId
 * @param  {boolean} isPlusPlan
 * @return {object}        plan info
 */
export function getSubscribePlanInfo(
  planId: string,
  isPlusPlan: boolean,
  allowToSubscribePlanV1: boolean = false
) {
  planId = planId ? planId.replace('_discount', '').toLowerCase() : planId;

  // Special process for plan offer: Password Protection
  if (planId === 'offer0_1') {
    return _.get(plansOffer, planId, {});
  }

  if (allowToSubscribePlanV1) {
    // Force mapping to plan V1
    planId = planId.replace('regular_', 'plan').replace('plus_', 'plan');
    return _.get(plansOld, planId, {});
  } else {
    // Map old plan to new -- Support old plan
    if (planId.includes('plan12_5')) {
      planId = planId.replace('plan12_5', isPlusPlan ? 'plus_15' : 'regular_15');
    }
    if (planId.includes('plan')) {
      planId = planId.replace('plan', isPlusPlan ? 'plus_' : 'regular_');
    }

    // Correct planId based on shopify plan for searching plan details
    if (isPlusPlan && _.startsWith(planId, 'regular')) {
      planId = planId.replace('regular', 'plus');
    }
    if (!isPlusPlan && _.startsWith(planId, 'plus')) {
      planId = planId.replace('plus', 'regular');
    }

    return isPlusPlan ? _.get(plansPlus, planId, {}) : _.get(plans, planId, {});
  }
}

/**
 * Get plan info based on planId
 *
 * @param  {string} planId
 * @return {object}        plan info
 */
export function getPlanInfo(planId: string) {
  planId = planId ? planId.replace('_discount', '').toLowerCase() : planId;

  // Special process for plan offer: Password Protection
  if (planId === 'offer0_1') {
    return _.get(plansOffer, planId, {});
  }

  // Old plan
  if (planId && planId.includes('plan')) {
    return _.get(plansOld, planId, {});
  }
  // Plus plan
  if (planId && planId.includes('plus_')) {
    return _.get(plansPlus, planId, {});
  }
  // Regular plan
  return _.get(plans, planId, {});
}

/**
 * Check if the store applied special discount code
 *
 * @param  {string} tenantId
 * @param  {string} selectedPlan
 * @param  {string} discountCode
 *
 * @return {boolean}              true | false
 */
function appliedSpecialDiscountCode(tenantId: string, selectedPlan: string, discountCode: Object) {
  if (!selectedPlan || !selectedPlan.includes('_discount')) return false;
  const code = _.get(discountCode, 'code', '');
  return code.includes('PREMIUM23');
}

/**
 * Check discount expired from n days
 * @param {string} status
 * @param {number} expiredAt
 * @param {number} days
 * @returns
 */
export function isSpecialDiscount2021Expired(status: string, expiredAt: number): boolean {
  const today = nowTimestamp();

  const isSpecialDiscountExpired =
    status === 'specialDiscountExpired' && ((expiredAt && expiredAt <= today) || !expiredAt);

  return isSpecialDiscountExpired;
}

/**
 * Check premium store
 *
 * @param  {object} tenantApp
 *
 * @return {boolean}           is premium store or not
 */
export const checkPremiumStore = (tenantApp: Object) => {
  const { selectedPlan = '' } = tenantApp;

  // Check old plan because all old plans are not support premium
  if (selectedPlan.includes('plan')) return false;

  // Get plan from selected plan name
  const plan = getPlanInfo(selectedPlan);
  // Return premium support
  if (!_.isEmpty(plan)) {
    return plan.premiumSupport;
  }
  return false;
};

/**
 * Allow to subscribe special discount code BFCM20
 *
 * @param  {object} tenantApp
 * @param  {string} subscribeStatus
 *
 * @return {boolean}
 */
export const allowToSubscribeBFCM20Code = (tenantApp: Object) => {
  const selectedPlan = _.get(tenantApp, 'selectedPlan', '');
  const planName = _.get(tenantApp, 'externalInfo.plan_name', '');
  const allowToSubscribePlanV1 = _.get(tenantApp, 'allowToSubscribePlanV1', false);
  const now = nowTimestamp();
  const deadline = 1606867199; // Dec 01, 2020

  return now <= deadline &&
    planName === 'shopify_plus' &&
    _.startsWith(selectedPlan, 'plan') &&
    !allowToSubscribePlanV1
    ? true
    : false;
};

/**
 * Allow to subscribe special discount code for BFCM 2022
 * Trial user
 * - Current trial
 * - Expired
 * - Not paid or uninstalled
 * @param {*} tenantApp
 * @param {*} subscribeStatus
 * @returns
 */
export const allowToSubscribeBFCMCode = (tenantApp: Object): boolean => {
  const { notChargedBefore = false, selectedPlan = '', allowToSubscribePlanV1 = false } = tenantApp;
  const now = nowTimestamp();
  const startDate = getUnixTimestamp(new Date('00:00 AM, Oct 05, 2022')); // 00:00 AM, Oct 05, 2022
  const endDate = getUnixTimestamp(new Date('00:00 AM, Nov 06, 2022')); // 00:00 AM, Nov 06, 2022
  return (
    now >= startDate &&
    now <= endDate &&
    selectedPlan === '' &&
    notChargedBefore &&
    !allowToSubscribePlanV1
  );
};

/**
 * Suggest 3 months free usage
 * @param {*} tenantApp
 * @returns
 */
export const suggest3MonthFreeUsage = (tenantApp: Object): boolean => {
  const { selectedPlan = '', exceededPlan = false } = tenantApp;
  const offerStartAt = _.get(tenantApp, 'passwordProtectedOffer.startAt', 0);
  const isPasswordProtected = _.get(tenantApp, 'externalInfo.password_enabled', false);

  // Not applied offer AND enable password protected
  return selectedPlan && !exceededPlan && offerStartAt <= 0 && isPasswordProtected;
};

// Check if Shopify plan is a paid plan
export const checkShopifyPaidPlan = (planName: string, tenantId: string = ''): boolean => {
  return (
    ![
      'affiliate',
      'cancelled',
      'staff',
      'closed',
      'plus_partner_sandbox',
      'trial',
      'frozen',
      'paused',
      'fraudulent',
      'partner_test',
      'dormant'
    ].includes(planName) || tenantId.startsWith('bc-dev-v2')
  );
};
